import { WireType } from 'features/services/wireVerification/interfaces/wireValidation';
import * as yup from 'yup';

export const REQUIRED_FIELD_MESSAGE_TEXT = 'is a required field.';

export const requiredFieldMessage = (fieldName: string): string => `${fieldName} ${REQUIRED_FIELD_MESSAGE_TEXT}`;

export const propertySchema = yup.object().shape({
  propertyIdentificationMethod: yup.string(),
  address1: yup
    .string()
    .max(50, 'Maximum of 50 characters')
    .when('propertyIdentificationMethod', {
      is: (value: string) => value != 'apn',
      then: (schema) => schema.required(requiredFieldMessage('Address 1')),
      otherwise: (schema) => schema.nullable(true),
    }),
  address2: yup.string().max(50, 'Maximum of 50 characters'),
  apn: yup
    .string()
    .max(200, 'Maximum of 200 characters')
    .when('propertyIdentificationMethod', {
      is: (value: string) => value === 'apn',
      then: (schema) => schema.required(requiredFieldMessage('APN')),
      otherwise: (schema) => schema.nullable(true),
    }),
  city: yup
    .string()
    .max(50)
    .when('propertyIdentificationMethod', {
      is: (value: string) => value != 'apn',
      then: (schema) => schema.required(requiredFieldMessage('City')).min(2, 'Minimum of 2 characters'),
      otherwise: (schema) => schema.nullable(true),
    }),
  state: yup.string().min(2).max(2).required('State is a required field.'),
  postalCode: yup
    .string()
    .matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.')
    .when('propertyIdentificationMethod', {
      is: (value: string) => value != 'apn',
      then: (schema) => schema.required(requiredFieldMessage('Postal code is a required field')),
      otherwise: (schema) => schema.nullable(true),
    }),
  kyTaxCodeResult: yup.object().shape({
    taxCode: yup.string().max(10, 'Maximum of 10 characters'),
  }),
});

const emailAndPhoneValidationRules = (isPhoneNumberRequired = false, maxLength = 100) => ({
  emailAddress: yup
    .string()
    .trim()
    .email('Invalid Email, @ and . characters required.')
    .max(maxLength, `Maximum of ${maxLength} characters.`)
    .nullable(),
  phoneNumber: isPhoneNumberRequired
    ? yup
        .string()
        .test('is-10-digits', 'Incomplete Phone Number.', (value) => {
          if (!value || value.trim() === '') {
            return true; // Skip validation if value is empty
          }
          return /^\d{3}-\d{3}-\d{4}$/.test(value) || /^\d{10}$/.test(value);
        })
        .required('Phone Number is a required field')
    : yup.string().test('is-10-digits', 'Incomplete Phone Number.', (value) => {
        if (!value || value.trim() === '') {
          return true; // Skip validation if value is empty
        }
        return /^\d{3}-\d{3}-\d{4}$/.test(value) || /^\d{10}$/.test(value);
      }),
});
const showErrors = (
  emailAddress: string | null | undefined,
  phoneNumber: string | undefined,
  path: 'emailAddress' | 'phoneNumber',
) => {
  const isEmailFilled = emailAddress && emailAddress.trim() !== '';
  const isPhoneFilled = phoneNumber && phoneNumber.trim() !== '';

  if (isEmailFilled || isPhoneFilled) {
    return { isPassed: true };
  }

  // Return validation error on both fields
  return {
    path,
    message: 'Phone Number or Email is required.',
  };
};

export const orderInsightEmailSchema = (maxLength = 100) =>
  yup.object().shape({
    preferredContactMethod: yup.string(),
    emailAddress: yup.string().when('preferredContactMethod', {
      is: 'emailAddress',
      then: (schema) =>
        schema
          .required(requiredFieldMessage('Email Address'))
          .email('Invalid Email, @ and . characters required.')
          .max(maxLength, `Maximum of ${maxLength} characters.`),
      otherwise: (schema) => schema.nullable(true),
    }),
    phoneNumber: yup.string().when('preferredContactMethod', {
      is: 'phoneNumber',
      then: (schema) =>
        schema
          .required(requiredFieldMessage('Phone Number'))
          .test('is-10-digits', 'Incomplete Phone Number.', (value) => {
            if (!value || value.trim() === '') {
              return true;
            }
            return /^\d{3}-\d{3}-\d{4}$/.test(value) || /^\d{10}$/.test(value);
          }),
      otherwise: (schema) => schema.nullable(true),
    }),
  });

export const orderInsightPartySchema = (maxLength = 100) =>
  yup
    .object()
    .shape(emailAndPhoneValidationRules(false, maxLength))
    .test('email-error', 'Phone Number or Email is required.', function (values) {
      const { emailAddress, phoneNumber } = values;
      const test = showErrors(emailAddress, phoneNumber, 'emailAddress');

      if (test.isPassed) {
        return true;
      }

      return this.createError(test);
    })
    .test('phonenumber-error', 'Phone Number or Email is required.', function (values) {
      const { emailAddress, phoneNumber } = values;
      const test = showErrors(emailAddress, phoneNumber, 'phoneNumber');

      if (test.isPassed) {
        return true;
      }

      return this.createError(test);
    });

/* Party Group Validation */

const personalIDSchema = (maxLength = 40) =>
  yup.object().shape({
    firstName: yup
      .string()
      .max(maxLength, `Maximum of ${maxLength} characters`)
      .required(requiredFieldMessage('First Name')),
    middleName: yup.string().max(maxLength, `Maximum of ${maxLength} characters`),
    lastName: yup
      .string()
      .max(maxLength, `Maximum of ${maxLength} characters`)
      .required(requiredFieldMessage('Last Name')),
    suffix: yup.string().max(5, 'Maximum of 5 characters'),
    taxId: yup.string().max(9, 'Maximum of 9 characters'),
    ...emailAndPhoneValidationRules(),
    ssn: yup
      .string()
      .max(11, 'Maximum of 9 characters')
      .matches(/^$|(^(\*{3}|\d{3})-(\*{2}|\d{2})-(\*{4}|\d{4}))$/, 'Incomplete SSN'),
  });

export const buyerAndSellerSchema = personalIDSchema(40);
export const buyerAndSellerDrawerSchema = personalIDSchema(400);

export const lenderSchema = yup.object().shape({
  myLenders: yup
    .mixed()
    .test('mylenders', 'My Lenders is required', function () {
      const lenderTypeOptions = this.resolve(yup.ref('lenderTypeOptions'));
      const lenderAddressBookId = this.resolve(yup.ref('lenderAddressBookId'));

      if (lenderTypeOptions !== undefined && lenderTypeOptions !== null) {
        // lenderTypeOptions exists, so it must be a non-empty string
        return (
          lenderTypeOptions !== undefined &&
          lenderTypeOptions !== null &&
          ((typeof lenderTypeOptions === 'string' && lenderTypeOptions.trim() !== '') ||
            (typeof lenderTypeOptions === 'number' && !isNaN(lenderTypeOptions)))
        );
      } else if (lenderAddressBookId !== undefined && lenderAddressBookId !== null) {
        // lenderAddressBookId exists, so it must be a non-null number
        return (
          lenderAddressBookId !== undefined &&
          lenderAddressBookId !== null &&
          typeof lenderAddressBookId === 'number' &&
          !isNaN(lenderAddressBookId)
        );
      } else {
        // neither lenderTypeOptions nor lenderAddressBookId exist, so myLenders is allowed to be null or undefined
        return true;
      }
    })
    .nullable(),
  lenderTypeOptions: yup.string().nullable(),
  lenderAddressBookId: yup.number().nullable(),
  name: yup.string().max(255, 'Maximum of 255 characters').required(requiredFieldMessage('Company Name')),
  loanNumber: yup.string().max(100, 'Maximum of 100 characters'),
  loanAmount: yup.string().max(14, 'Maximum of 14 characters'),
  ...emailAndPhoneValidationRules(),
  attention: yup.string().nullable().max(100, 'Maximum of 100 characters'),
  lenderTypeClause: yup.string().max(255, 'Maximum of 255 characters'),
});

export const lenderSchemaWithoutLenderTypeOptions = yup.object().shape({
  name: yup.string().max(255, 'Maximum of 255 characters').required(requiredFieldMessage('Company Name')),
  loanNumber: yup.string().max(100, 'Maximum of 100 characters'),
  loanAmount: yup.string().max(14, 'Maximum of 14 characters'),
  phoneNumber: yup.string().nullable(),
  emailAddress: yup.string().trim().email('Invalid email.').max(100, 'Maximum of 100 characters.').nullable(),
  attention: yup.string().nullable().max(100, 'Maximum of 100 characters'),
  lenderTypeClause: yup.string().max(255, 'Maximum of 255 characters'),
});

export const lenderSchemaForNameOnly = yup.object().shape({
  name: yup.string().trim().max(255, 'Maximum of 255 characters').required(requiredFieldMessage('Company Name')),
  ...emailAndPhoneValidationRules(),
});

export const fileSchema = yup.object().shape({
  accountId: yup.number().required('Account Id is a required field'),
  closingDate: yup
    .date()
    .nullable()
    .min('0500-01-01')
    .max('2100-01-01')
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError(() => 'Invalid Date'),
  fileNumber: yup.string().max(40, 'Maximum of 40 characters').required(requiredFieldMessage('File number')),
  fileStatus: yup.string().max(40, 'Maximum of 40 characters').required(requiredFieldMessage('File Status')),
  firmId: yup.string().max(40, 'Maximum of 40 characters'),
  itemType: yup.string().max(40, 'Maximum of 40 characters'),
  loanAmount: yup.number(),
  officeId: yup.string().max(40, 'Maximum of 40 characters'),
  properties: yup.array().of(propertySchema).compact(),
  propertyState: yup.string().max(2, 'Maximum of 40 characters'),
  salesPrice: yup.number(),
  office: yup.string().required(requiredFieldMessage('Office')),
  transactionType: yup.string().max(40, 'Maximum of 40 characters'),
});

export const createPropertySchema = yup.object().shape({
  propertyIdentificationMethod: yup.string().required('Property Identification Method is a required field.'),
  propertyType: yup.string().required('NAIC is a required field.'),
  apn: yup
    .string()
    .max(200, 'Maximum of 200 characters')
    .when('propertyIdentificationMethod', {
      is: (value: string) => value === 'apn',
      then: (schema) => schema.required(requiredFieldMessage('APN')),
      otherwise: (schema) => schema.nullable(true),
    }),
  kyTaxCodeResult: yup.object().shape({
    taxCode: yup.string().max(10, 'Maximum of 10 characters'),
  }),
});

export const nameSchema = yup.object().shape({
  name: yup.string().max(130, 'Maximum of 130 characters').required(requiredFieldMessage('Name')),
});

export const partyTypesSchema = yup.object().shape({
  name: yup.string().max(130, 'Maximum of 130 characters').required(requiredFieldMessage('Name')),
  taxId: yup
    .string()

    .matches(/^$|(^(\*{2}|\d{2})-(\*{7}|\d{7}))$/, 'Valid Tax ID Numbers Required'),
});
export const buyerSellerSchema = yup.object().shape({
  partyType: yup.string().required(requiredFieldMessage('Party Type')),
});

export const partiesMethodSchema = yup.object().shape({
  partyMethod: yup.string().required(requiredFieldMessage('Party')),
});

export const buyerSellerDometicAddressSchema = yup.object().shape({
  address1: yup.string().max(55, 'Maximum of 55 characters'),
  address2: yup.string().optional().max(55, 'Maximum of 55 characters'),
  city: yup.string().optional().max(50, 'Maximum of 50 characters'),
  postalCode: yup
    .string()
    .max(10, 'Maximum of 9 characters')
    .matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.'),
});

export const fileInfoCreateFormSchema = yup.object().shape({
  officeId: yup.mixed().test('is-required', 'Office is a required field.', (value: any) => {
    if (!isNaN(value)) {
      return value > 10;
    }
    return !(value === undefined || value === null || value === '');
  }),
  stateCode: yup.string().required('State is a required field.'),
  underwriterCode: yup.string().required('Underwriter is a required field.'),
  closingDate: yup
    .date()
    .nullable()
    .min('0500-01-01')
    .max('2100-01-01')
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Invalid Date'),
  fileNumber: yup.string().max(40, 'Maximum of 40 characters').required('File number is a required field.'),
  transactionType: yup.string().required('Transaction Type is a required field.'),
});

export const fileInfoEditFormSchema = yup.object().shape({
  officeId: yup.number().min(10, 'Office is a required field').required('Office is a required field.'),
  underwriterCode: yup.string().required('Underwriter is a required field.'),
  closingDate: yup
    .date()
    .nullable()
    .min('0500-01-01')
    .max('2100-01-01')
    .transform((curr, orig) => (orig === '' ? null : curr))
    .typeError('Invalid Date'),
  fileNumber: yup.string().max(40, 'Maximum of 40 characters').required('File number is a required field.'),
  transactionType: yup.string(),
});

export const partyFileInfoValidation = yup.object().shape({
  underwriterCode: yup.string().required('Underwriter is a required field.'),
  fileNumber: yup.string().max(40, 'Maximum of 40 characters').required('File number is a required field.'),
});

const addressSchema = (addressMaxLength = 55, cityMaxLength = 50) =>
  yup.object().shape({
    address1: yup.string().max(addressMaxLength, `Maximum of ${addressMaxLength} characters`),
    address2: yup.string().nullable().max(addressMaxLength, `Maximum of ${addressMaxLength} characters`),
    city: yup.string().optional().max(cityMaxLength, `Maximum of ${cityMaxLength} characters`),
    postalCode: yup.string().matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.'),
  });

export const domesticAddressSchema = addressSchema();

export const domesticAddressDrawerSchema = addressSchema(400, 400);

export const domesticAddressSchemaRequired = yup.object().shape({
  address1: yup.string().max(55, 'Maximum of 55 characters').required(requiredFieldMessage('Address 1')),
  address2: yup.string().optional().max(55, 'Maximum of 55 characters'),
  city: yup.string().max(50, 'Maximum of 50 characters').required(requiredFieldMessage('City')),
  postalCode: yup
    .string()
    .matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.')
    .required(requiredFieldMessage('Zip')),
});

export const lenderDomesticAddressSchema = yup.object().shape({
  address1: yup.string().max(55, 'Maximum of 55 characters').required(requiredFieldMessage('Address 1')),
  address2: yup.string().nullable().max(55, 'Maximum of 55 characters'),
  city: yup.string().max(50, 'Maximum of 50 characters').required(requiredFieldMessage('City')),
  stateOrProvince: yup.string().required(requiredFieldMessage('State')),
  postalCode: yup
    .string()
    .matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.')
    .required(requiredFieldMessage('Zip Code')),
});

export const propertyDomesticAddressSchema = yup.object().shape({
  address1: yup.string().max(55, 'Maximum of 55 characters').required(requiredFieldMessage('Address 1')),
  address2: yup.string().optional().max(55, 'Maximum of 55 characters'),
  city: yup.string().max(50, 'Maximum of 50 characters').required(requiredFieldMessage('City')),
  state: yup.string().required(requiredFieldMessage('State')),
  county: yup.string().required(requiredFieldMessage('County')),
  postalCode: yup
    .string()
    .matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.')
    .required(requiredFieldMessage('Zip Code')),
});

export const apnAddressSchema = yup.object().shape({
  address1: yup.string().max(55, 'Maximum of 55 characters'),
  address2: yup.string().optional().max(55, 'Maximum of 55 characters'),
  city: yup.string().max(50, 'Maximum of 50 characters'),
  state: yup.string().required(requiredFieldMessage('State')),
  county: yup.string().required(requiredFieldMessage('County')),
  postalCode: yup.string().matches(/(^$|^\b\d{5}(-\d{4})?\b$)/, 'Invalid zip code, 5 or 9 characters required.'),
});

export const foreignAddressSchema = yup.object().shape({
  address1: yup.string().max(55, 'Maximum of 55 characters'),
  address2: yup.string().max(55, 'Maximum of 55 characters'),
  city: yup.string().max(50, 'Maximum of 50 characters'),
  country: yup
    .string()
    .max(50, 'Maximum of 50 characters')
    .matches(/^[a-zA-Z0-9 ]*$/, 'No special characters'),
  stateOrProvince: yup.string().max(50, 'Maximum of 50 characters'),
  postalCode: yup.string().max(25, 'Maximum of 25 characters'),
});

export const taxIdSchema = yup.object().shape({
  taxId: yup.object({
    value: yup
      .string()
      .matches(/^\d{0}$|^\d{2}-\d{7}$|^\d{0}$|^\d{3}-\d{2}-\d{4}|^\d{9}$/g, 'Valid Tax ID Numbers Required'),
  }),
});

export const wireVerificationSchema = yup.object().shape({
  underwriterCode: yup.string().required(requiredFieldMessage('Underwriter')),
  fileNumber: yup.string().max(40, 'Maximum of 40 characters').required(requiredFieldMessage('File Number')),
  wireType: yup.string(),
  firstName: yup
    .string()
    .max(40, 'Maximum of 40 characters')
    .when('wireType', {
      is: (value: string) => value === WireType.PERSONAL,
      then: (schema) => schema.required(requiredFieldMessage('First Name')),
      otherwise: (schema) => schema.nullable(true),
    }),
  lastName: yup
    .string()
    .max(40, 'Maximum of 40 characters')
    .when('wireType', {
      is: (value: string) => value === WireType.PERSONAL,
      then: (schema) => schema.required(requiredFieldMessage('Last Name')),
      otherwise: (schema) => schema.nullable(true),
    }),
  payeeName: yup
    .string()
    .max(130, 'Maximum of 130 characters')
    .when('wireType', {
      is: (value: string) => value === WireType.BUSINESS,
      then: (schema) => schema.required(requiredFieldMessage('Payee Name')),
      otherwise: (schema) => schema.nullable(true),
    }),
  receivingBankName: yup
    .string()
    .max(255, 'Maximum of 255 characters')
    .required(requiredFieldMessage('Receiving Bank Name')),
  wireAmount: yup.string().required(requiredFieldMessage('Wire Amount')),
  routingNumber: yup
    .string()
    .required(requiredFieldMessage('Routing Number'))
    .matches(/^\d{9}$/, 'Routing Number must be exactly 9 digits.'),
  firmId: yup
    .string()
    .required(requiredFieldMessage('Account Number'))
    .matches(/^\d{3,18}$/, 'Account Number must be between 3 and 18 digits.'),
});
